import Phaser from 'phaser';
import {
  CAMO_WARN_TICKS,
  MATCH_INTERMISSION,
  MODE_TEAM,
  NO_TEAM,
  PROP_AMMO,
  PROP_BRICK,
  PROP_COUNT,
  PROP_CRATE,
  PROP_HEALTH,
  RESPAWN_TICKS,
  TICK_RATE,
  type LevelData,
  type ScoreEntry,
  type World,
  MATCH_WARMUP,
  WEAPON_GRENADE,
} from '@mayday/shared';
import { UNPIN_BOARD } from './lobby';
import { BRASS, Feed, INK_DIM, MapPreview, Scoreboard, TouchControls, buildZoomButtons, hudText, platform } from '@platoon/client-kit';
import { SETTINGS_CHANGED, setCamoProp, setShowDebug, settings } from './settings';

// Heads-up display, layered over the game scene. Layout follows the classic
// shooter arrangement (see AGENTS.md "HUD"):
//
//   top-left     compass-style north-up minimap, event feed to its right
//   top-right    score
//   bottom-left  health
//   bottom-right weapon + ammo
//
// The game scene publishes state through the registry (level, world, myId,
// health, ammo, score ...) and pushes feed lines via `game.events`
// ('hud:log'). This scene only reads; it never touches the simulation.

export const HUD_LOG = 'hud:log';
// Emitted on game.events when the player asks for the options dialog from
// the HUD (gear on the scoreboard); main.ts opens the DOM panel.
export const OPEN_MENU = 'ui:openMenu'; // the ☰ button or the scoreboard's menu entry
// Emitted on game.events with (text, 'toast' | 'overlay') for a DOM notice
// over the canvas (server update on the way, reloading); main.ts shows it.
export const UI_NOTICE = 'ui:notice';
// Emitted on game.events with +1 / -1 / 0 (reset) by the touch zoom buttons.
export const UI_ZOOM = 'ui:zoom';
export const START_MATCH = 'ui:start'; // warm-up: the board's Start button

const MARGIN = 16;
const MINIMAP_MARGIN = 8;
// Team names and colours (same palette as the scoreboard).
const TEAM_SHORT: Record<number, string> = { 1: 'Blue', 2: 'Red', 3: 'Green', 4: 'Gold' };
const TEAM_DOT: Record<number, number> = { 1: 0x5b8fd6, 2: 0xd65b5b, 3: 0x6fbf73, 4: 0xd6b25b };

// What GameScene publishes under registry 'match' from MSG_MATCH.
interface MatchInfo {
  state: number;
  winner: number;
  winnerName: string;
  mode: number;
  winnerTeam: number;
  killLimit: number;
  timeLimitS: number;
  secondsLeft: number;
  at: number; // scene time when it arrived
  teams: number;
  teamSize: number;
}

export class HudScene extends Phaser.Scene {
  private level!: LevelData;
  private world!: World;

  // map preview (kit) and event feed (kit)
  private preview!: MapPreview;
  private feed!: Feed;

  // corners
  private scoreText!: Phaser.GameObjects.Text;
  private clockText!: Phaser.GameObjects.Text; // match time left, under the score
  private healthBar!: Phaser.GameObjects.Graphics;
  private healthText!: Phaser.GameObjects.Text;
  private healthLabel!: Phaser.GameObjects.Text;
  private weaponText!: Phaser.GameObjects.Text;
  private ammoText!: Phaser.GameObjects.Text;
  private debugText!: Phaser.GameObjects.Text;
  private deadText!: Phaser.GameObjects.Text;
  private touch: TouchControls | null = null;

  // camouflage: icon at the bottom centre, chooser above it
  private camoIcon!: Phaser.GameObjects.Image;
  private camoFrame!: Phaser.GameObjects.Graphics;
  private camoTimer!: Phaser.GameObjects.Text;
  private camoLabel!: Phaser.GameObjects.Text;
  private camoChooser!: Phaser.GameObjects.Container;
  private camoChoices: Phaser.GameObjects.Image[] = [];
  private camoChooserOpen = false;

  // scoreboard: held with Tab; click/tap the score to pin, Tab unpins
  private scoreboard!: Scoreboard;
  private tabKey!: Phaser.Input.Keyboard.Key;
  private scoreboardPinned = false;
  private scoresSeen: ScoreEntry[] | null = null;
  private bannerSeen = '';
  private warmupDismissed = false; // the player closed the warm-up board with Tab

  constructor() {
    super({ key: 'hud', active: false });
  }

  create(): void {
    this.level = this.registry.get('level') as LevelData;
    this.world = this.registry.get('world') as World;

    this.preview = new MapPreview(this, { margin: MINIMAP_MARGIN });
    this.preview.build(this.level);
    this.feed = new Feed(this, { x: MINIMAP_MARGIN + this.preview.size.w + 12, y: MARGIN });
    // A live level swap publishes the new level; redraw the preview.
    this.registry.events.on('changedata-level', (_p: unknown, next: LevelData) => {
      this.level = next;
      this.preview.build(next);
      this.feed.setX(MINIMAP_MARGIN + this.preview.size.w + 12);
      this.layout();
    });

    this.scoreText = this.text(0, 0, '', 18).setOrigin(1, 0);
    this.clockText = this.text(0, 0, '', 16, INK_DIM).setOrigin(1, 0);
    this.healthBar = this.add.graphics();
    this.healthLabel = this.text(0, 0, 'Health', 14, INK_DIM);
    this.healthText = this.text(0, 0, '100', 22);
    this.weaponText = this.text(0, 0, '', 14, INK_DIM).setOrigin(1, 0);
    this.ammoText = this.text(0, 0, '', 22).setOrigin(1, 0);
    this.debugText = this.text(0, 0, '', 11, INK_DIM).setOrigin(0.5, 1).setAlpha(0.8);
    this.deadText = this.text(0, 0, 'You were killed\nrespawning…', 26, '#e07a7a').setOrigin(0.5).setAlign('center').setVisible(false);
    // Hurt vignette: red edges that fade over HURT_MS when we take damage,
    // strength by damage. Never a full-screen wash: the fight stays visible.
    this.makeVignette();
    this.vignette = this.add.image(0, 0, 'vignette').setOrigin(0).setDepth(-10).setAlpha(0);

    // Phones/tablets: on-screen twin sticks. The game scene reads the state
    // object from the registry every tick.
    if (platform.isTouchPrimary()) {
      this.touch = new TouchControls(this);
      this.registry.set('touch', this.touch.state);
    }

    // HUD clicks must not double as shots: the game scene reads the raw
    // pointer, so flag the pointer as consumed until it is released.
    const consumePointer = () => this.registry.set('uiPointerBlock', true);
    this.input.on(Phaser.Input.Events.POINTER_UP, () => this.registry.set('uiPointerBlock', false));

    this.scoreboard = new Scoreboard(this, {
      title: 'MAYDAY PLATOON',
      onOptions: () => {
        consumePointer();
        this.scoreboardPinned = false; // the board gives way to the menu
        this.game.events.emit(OPEN_MENU);
      },
      onStart: () => this.game.events.emit(START_MATCH),
      onInvite: (roomId) => {
        this.scoreboardPinned = false; // copied: the board closes
        consumePointer();
        const link = platform.inviteLink(roomId);
        void platform.copyText(link).then((ok) => {
          this.game.events.emit(UI_NOTICE, ok ? `Invite link copied:\n${link}` : `Invite link:\n${link}`, 'toast', 6000);
        });
      },
    });
    const kb = this.input.keyboard!;
    kb.addKey(Phaser.Input.Keyboard.KeyCodes.F3).on('down', () => {
      setShowDebug(!settings.showDebug);
      this.game.events.emit(SETTINGS_CHANGED);
    });
    this.tabKey = kb.addKey(Phaser.Input.Keyboard.KeyCodes.TAB);
    kb.addCapture(Phaser.Input.Keyboard.KeyCodes.TAB); // keep focus from leaving the canvas
    // A pinned board (clicked Score) closes on Tab: the press unpins it, so
    // it shows while Tab is held and goes away on release like a normal peek.
    this.tabKey.on('down', () => {
      this.scoreboardPinned = false;
      this.warmupDismissed = true; // the warm-up board shows itself once; Tab peeks after that
    });
    this.scoreText.setInteractive({ useHandCursor: true }).on(Phaser.Input.Events.POINTER_DOWN, () => {
      consumePointer();
      this.scoreboardPinned = !this.scoreboardPinned;
    });
    // The menu asks for a pinned board to drop (Esc closes the board first).
    const unpin = () => {
      this.scoreboardPinned = false;
    };
    this.game.events.on(UNPIN_BOARD, unpin);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.game.events.off(UNPIN_BOARD, unpin));
    // Touch: ☰ at the top right beside the score (the corner convention of
    // mobile shooters: a rare action, out of the thumb zones; docs/screens/menu.md).
    if (this.touch) {
      this.menuButton = this.add.circle(0, 0, 18, 0x070a08, 0.6).setStrokeStyle(1, BRASS, 0.8).setDepth(50);
      this.menuButtonLabel = this.text(0, 0, '☰', 18, '#e8d7a8').setOrigin(0.5).setDepth(51);
      this.menuButton.setInteractive({ useHandCursor: true }).on(Phaser.Input.Events.POINTER_DOWN, () => {
        consumePointer();
        this.game.events.emit(OPEN_MENU);
      });
    }

    this.buildCamoWidget(consumePointer);
    if (this.touch) {
      this.zoomButtons = buildZoomButtons(this, consumePointer, (delta) => this.game.events.emit(UI_ZOOM, delta));
      // Rifle / grenade toggle (switch model, docs/controls.md): a tap, then
      // the aim stick throws, its deflection setting the distance.
      this.weaponButton = this.add.circle(0, 0, 26, 0x070a08, 0.65).setStrokeStyle(2, 0xffd27a, 0.9).setDepth(50);
      this.weaponButtonLabel = this.text(0, 0, '✶', 24, '#ffd27a').setOrigin(0.5).setDepth(51);
      this.weaponButton.setInteractive({ useHandCursor: true }).on(Phaser.Input.Events.POINTER_DOWN, () => {
        consumePointer();
        this.registry.set('touchSwitch', true);
      });
    }

    this.layout();
    this.scale.on(Phaser.Scale.Events.RESIZE, this.layout, this);
    this.game.events.on(HUD_LOG, this.log, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off(Phaser.Scale.Events.RESIZE, this.layout, this);
      this.game.events.off(HUD_LOG, this.log, this);
    });
  }

  override update(time: number): void {
    this.updateMinimap();
    this.feed.update(time);
    this.updateCamo();
    this.touch?.draw();

    const r = this.registry;
    const health = (r.get('health') as number | undefined) ?? 100;
    this.healthText.setText(String(health));
    const hurt = r.get('hurt') as { at: number; strength: number } | undefined;
    const hurtT = hurt ? 1 - (time - hurt.at) / HudScene.HURT_MS : 0;
    this.vignette.setAlpha(hurt && hurtT > 0 ? 0.6 * hurt.strength * hurtT : 0);
    this.drawHealthBar(health);
    const match = r.get('match') as MatchInfo | null | undefined;
    const intermission = match?.state === MATCH_INTERMISSION;
    const warmup = match?.state === MATCH_WARMUP;
    if (!warmup) this.warmupDismissed = false;
    // Seconds left in the current phase, counted down locally between the
    // server's 1 Hz updates.
    const secondsLeft = match ? Math.max(0, Math.ceil(match.secondsLeft - (time - match.at) / 1000)) : 0;
    const score = (r.get('score') as number | undefined) ?? 0;
    if (match?.mode === MODE_TEAM) {
      // Team totals from the roster: "Blue 7 · Red 5 / 20", own team first.
      const scores = (r.get('scores') as ScoreEntry[] | undefined) ?? [];
      const mine = (r.get('myTeam') as number | undefined) ?? NO_TEAM;
      const totals = new Map<number, number>();
      for (const e of scores) totals.set(e.team, (totals.get(e.team) ?? 0) + e.kills);
      const parts = [...totals.entries()].sort((a, b) => (a[0] === mine ? -1 : b[0] === mine ? 1 : a[0] - b[0])).map(([t, k]) => `${TEAM_SHORT[t] ?? `T${t}`} ${k}`);
      this.scoreText.setText(`${parts.join(' · ')} / ${match.killLimit}`);
    } else {
      this.scoreText.setText(match && match.killLimit > 0 ? `Score: ${score} / ${match.killLimit}` : `Score: ${score}`);
    }
    if (warmup) {
      this.scoreText.setText('WARM-UP');
      this.clockText.setText(`starts in ${secondsLeft}`).setColor('#e0c060');
    } else if (match && !intermission && match.timeLimitS > 0) {
      const mm = Math.floor(secondsLeft / 60);
      const ss = secondsLeft % 60;
      this.clockText.setText(`${mm}:${ss < 10 ? '0' : ''}${ss}`).setColor(secondsLeft <= 60 ? '#e0c060' : INK_DIM);
    } else {
      this.clockText.setText('');
    }
    const reloading = (r.get('reloading') as boolean | undefined) ?? false;
    // Weapon panel: what is in hand, with the other weapon's count beside it.
    const grenadeOut = r.get('weaponId') === WEAPON_GRENADE;
    const switching = r.get('switching') === true;
    const grenades = (r.get('grenades') as number | undefined) ?? 0;
    const switchKey = this.touch ? '  ·  ' : `  ${settings.bindings.switchWeapon.label}: `;
    if (grenadeOut) {
      this.weaponText.setText(switching ? 'TAKING OUT A GRENADE…' : `GRENADE${switchKey}rifle ${(r.get('ammo') as number | undefined) ?? 0} | ${(r.get('ammoReserve') as number | undefined) ?? 0}`);
      this.ammoText.setText(`✶ ${grenades}`);
    } else {
      this.weaponText.setText(switching ? 'SHOULDERING…' : reloading ? 'RELOADING' : `${(r.get('weapon') as string | undefined) ?? ''}${switchKey}${grenades} grenade${grenades === 1 ? '' : 's'}`);
      this.ammoText.setText(`${(r.get('ammo') as number | undefined) ?? 0} | ${(r.get('ammoReserve') as number | undefined) ?? 0}`);
    }
    this.weaponButton?.setData('grenade', grenadeOut);
    this.weaponButtonLabel?.setText(grenadeOut ? '▮' : '✶').setColor(grenadeOut ? '#e8d7a8' : '#ffd27a');
    const dead = ((r.get('dead') as boolean | undefined) ?? false) && !intermission;
    const shielded = ((r.get('protected') as boolean | undefined) ?? false) && !intermission;
    this.deadText.setVisible(dead || shielded);
    if (dead) {
      // Who did it, and 3-2-1 until the server puts us back on the field.
      const info = r.get('deathInfo') as { killer: string; at: number } | undefined;
      const elapsed = info ? (time - info.at) / 1000 : 0;
      const left = Math.max(1, Math.ceil(RESPAWN_TICKS / TICK_RATE - elapsed));
      const by = info ? `Killed by ${info.killer}` : 'You were killed';
      this.deadText.setText(`${by}\n\nRespawning in ${left}`).setColor('#e07a7a').setFontSize(26);
    } else if (shielded) {
      this.deadText.setText('SPAWN PROTECTION').setColor('#9fe0ff').setFontSize(18);
    }
    this.debugText.setText(settings.showDebug ? ((r.get('debug') as string | undefined) ?? '') : '');

    // Scoreboard data refreshes once a second from the server; only rebuild
    // the table when a new roster arrived.
    // During the intermission the board is forced open with the result and
    // a countdown; the banner changes once a second, so rebuild on change.
    let banner = '';
    let bannerDetail = '';
    if (match && intermission) {
      banner = 'MATCH OVER';
      const myTeam = (r.get('myTeam') as number | undefined) ?? NO_TEAM;
      const who =
        match.mode === MODE_TEAM
          ? match.winnerTeam === NO_TEAM
            ? 'Draw'
            : `${TEAM_SHORT[match.winnerTeam] ?? `Team ${match.winnerTeam}`} team wins${match.winnerTeam === myTeam ? ' — that is you' : ''}`
          : match.winnerName
            ? `${match.winnerName} ${match.winner === r.get('myId') ? 'win' : 'wins'}`
            : 'Draw';
      bannerDetail = `${who}   ·   next match in ${secondsLeft}`;
    } else if (match && warmup) {
      banner = 'WARM-UP';
      bannerDetail = `Match starts in ${secondsLeft} s, or when every slot has a player.   Invite friends with the link.`;
    }
    const scores = r.get('scores') as ScoreEntry[] | undefined;
    const bannerKey = banner + bannerDetail + ((r.get('room') as string | undefined) ?? '');
    if (scores && (scores !== this.scoresSeen || bannerKey !== this.bannerSeen)) {
      this.scoresSeen = scores;
      this.bannerSeen = bannerKey;
      this.scoreboard.setData(scores, (r.get('myId') as number | undefined) ?? -1, {
        levelName: this.level.name,
        mode: (r.get('mode') as string | undefined) ?? '',
        roomId: (r.get('room') as string | undefined) ?? '',
        banner,
        bannerDetail,
        startLabel: warmup ? '▶ Start now' : undefined,
        teamIds: match?.mode === MODE_TEAM && match.teams > 0 ? Array.from({ length: match.teams }, (_, i) => i + 1) : undefined,
        slotsPerTeam: match?.mode === MODE_TEAM ? match.teamSize : undefined,
      });
    }
    this.registry.set('boardPinned', this.scoreboardPinned);
    // ☰ just left of the score text, whatever its width this frame.
    if (this.menuButton) {
      const bx = this.scoreText.x - this.scoreText.displayWidth - 30;
      this.menuButton.setPosition(bx, MARGIN + 14);
      this.menuButtonLabel?.setPosition(bx, MARGIN + 13);
    }
    this.scoreboard.setVisible(this.tabKey.isDown || this.scoreboardPinned || intermission || (warmup && !this.warmupDismissed));
  }

  // ---- layout ---------------------------------------------------------------

  private static readonly HURT_MS = 350;
  private vignette!: Phaser.GameObjects.Image;

  private makeVignette(): void {
    if (this.textures.exists('vignette')) return;
    const size = 256;
    const tex = this.textures.createCanvas('vignette', size, size);
    if (!tex) return;
    const ctx = tex.getContext();
    const grad = ctx.createRadialGradient(size / 2, size / 2, size * 0.28, size / 2, size / 2, size * 0.7);
    grad.addColorStop(0, 'rgba(200,30,20,0)');
    grad.addColorStop(1, 'rgba(200,30,20,1)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);
    tex.refresh();
  }

  private layout(): void {
    const w = this.scale.width;
    const h = this.scale.height;
    this.scoreText.setPosition(w - MARGIN, MARGIN);
    this.clockText.setPosition(w - MARGIN, MARGIN + 24);


    const barH = 90;
    const barW = 18;
    const barX = MARGIN;
    const barY = h - MARGIN - barH;
    this.healthBar.setPosition(barX, barY);
    this.healthLabel.setPosition(barX + barW + 10, barY + barH - 46);
    this.healthText.setPosition(barX + barW + 10, barY + barH - 28);

    this.weaponText.setPosition(w - MARGIN, h - MARGIN - 46);
    this.ammoText.setPosition(w - MARGIN, h - MARGIN - 28);
    this.debugText.setPosition(w / 2, h - 6);
    this.deadText.setPosition(w / 2, h * 0.4);
    this.vignette.setDisplaySize(w, h);
    this.layoutCamo(w, h);
    this.scoreboard.relayout();
  }

  // ---- camouflage widget ----------------------------------------------------

  private static readonly PROPS: Array<{ prop: number; key: string; frame?: string; label: string }> = [
    { prop: PROP_BRICK, key: 'tiles', frame: 'wall', label: 'Wall' },
    { prop: PROP_CRATE, key: 'tiles', frame: 'crate', label: 'Crate' },
    { prop: PROP_HEALTH, key: 'kit-health', label: 'Health kit' },
    { prop: PROP_AMMO, key: 'kit-ammo', label: 'Ammo crate' },
  ];

  private buildCamoWidget(consumePointer: () => void): void {
    this.camoFrame = this.add.graphics();
    this.camoIcon = this.add.image(0, 0, 'kit-camo').setInteractive({ useHandCursor: true });
    this.camoLabel = this.text(0, 0, '', 11, INK_DIM).setOrigin(0.5, 0);
    this.camoTimer = this.text(0, 0, '', 22, '#ffd27a').setOrigin(0.5, 1);

    // Chooser: the four props in a row above the icon, current one framed.
    this.camoChooser = this.add.container(0, 0).setVisible(false).setDepth(150);
    const bg = this.add.graphics();
    bg.fillStyle(0x070a08, 0.85).fillRoundedRect(-104, -30, 208, 60, 6);
    bg.lineStyle(1, BRASS, 1).strokeRoundedRect(-104, -30, 208, 60, 6);
    this.camoChooser.add(bg);
    HudScene.PROPS.forEach((p, i) => {
      const x = -78 + i * 52;
      const img = this.add.image(x, -4, p.key, p.frame).setInteractive({ useHandCursor: true });
      const scale = Math.min(1, 30 / Math.max(img.width, img.height));
      img.setScale(scale);
      img.on(Phaser.Input.Events.POINTER_DOWN, () => {
        consumePointer();
        setCamoProp(p.prop);
        this.game.events.emit(SETTINGS_CHANGED);
        this.setChooserOpen(false);
      });
      this.camoChooser.add(img);
      this.camoChooser.add(this.text(x, 14, p.label, 10, INK_DIM).setOrigin(0.5, 0));
      this.camoChoices.push(img);
    });

    this.camoIcon.on(Phaser.Input.Events.POINTER_DOWN, () => {
      consumePointer();
      this.setChooserOpen(!this.camoChooserOpen);
    });
  }

  private setChooserOpen(open: boolean): void {
    this.camoChooserOpen = open;
    this.camoChooser.setVisible(open);
  }

  // Touch devices: − / + buttons above the ammo corner (kit component).
  private zoomButtons: Phaser.GameObjects.Container | null = null;
  private menuButton: Phaser.GameObjects.Arc | null = null;
  private menuButtonLabel: Phaser.GameObjects.Text | null = null;
  private weaponButton: Phaser.GameObjects.Arc | null = null;
  private weaponButtonLabel: Phaser.GameObjects.Text | null = null;

  private layoutCamo(w: number, h: number): void {
    this.zoomButtons?.setPosition(w - MARGIN - 60, h - MARGIN - 110);
    this.weaponButton?.setPosition(w - MARGIN - 34, h - MARGIN - 180);
    this.weaponButtonLabel?.setPosition(w - MARGIN - 34, h - MARGIN - 182);
    const cx = w / 2;
    const cy = h - MARGIN - 34;
    this.camoIcon.setPosition(cx, cy);
    this.camoLabel.setPosition(cx, cy + 16);
    this.camoTimer.setPosition(cx, cy - 18);
    this.camoChooser.setPosition(cx, cy - 64);
  }

  private updateCamo(): void {
    const r = this.registry;
    const ticks = (r.get('camoTicks') as number | undefined) ?? 0;
    const prop = (r.get('camoProp') as number | undefined) ?? settings.camoProp;
    const charged = ticks > 0;
    const info = HudScene.PROPS.find((p) => p.prop === prop);

    this.camoIcon.setAlpha(charged ? 1 : 0.45);
    this.camoLabel.setText(charged ? `camo: ${info?.label ?? '?'}` : info ? `camo: ${info.label}` : 'camo: click to choose');

    // Countdown for the last few seconds, blinking.
    if (charged && ticks <= CAMO_WARN_TICKS) {
      this.camoTimer.setText(String(Math.ceil(ticks / TICK_RATE))).setVisible(((this.time.now / 250) | 0) % 2 === 0);
    } else {
      this.camoTimer.setVisible(false);
    }

    const g = this.camoFrame;
    g.clear();
    if (charged) {
      // Thin arc showing the remaining charge around the icon.
      const frac = ticks / (30 * TICK_RATE);
      g.lineStyle(3, 0xffd27a, 0.9);
      g.beginPath();
      g.arc(this.camoIcon.x, this.camoIcon.y, 19, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * frac, false);
      g.strokePath();
    }
    // Chooser highlight on the current choice.
    if (this.camoChooserOpen) {
      for (let i = 0; i < PROP_COUNT; i++) {
        const img = this.camoChoices[i]!;
        const chosen = HudScene.PROPS[i]!.prop === prop;
        img.setTint(chosen ? 0xffffff : 0xa0a0a0);
        if (chosen) {
          const b = img.getBounds();
          g.lineStyle(2, BRASS, 1).strokeRect(b.x - 4, b.y - 4, b.width + 8, b.height + 8);
        }
      }
    }
  }

  private drawHealthBar(health: number): void {
    const g = this.healthBar;
    const barH = 90;
    const barW = 18;
    const t = Phaser.Math.Clamp(health / 100, 0, 1);
    g.clear();
    g.fillStyle(0x000000, 0.5).fillRect(0, 0, barW, barH);
    const color = t > 0.5 ? 0x5fbf5a : t > 0.25 ? 0xd9a531 : 0xc9403a;
    g.fillStyle(color, 1).fillRect(2, 2 + (barH - 4) * (1 - t), barW - 4, (barH - 4) * t);
    g.lineStyle(2, BRASS, 1).strokeRect(0, 0, barW, barH);
  }

  // ---- map preview -------------------------------------------------------------

  // Red dots for enemies inside the view radius (never disguised ones, so
  // camouflage keeps working); teammates in team colour, map-wide (the
  // server always sends them). 0 = not drawn.
  private updateMinimap(): void {
    const myId = (this.registry.get('myId') as number | undefined) ?? -1;
    const w = this.world;
    const myTeam = myId >= 0 ? w.team[myId]! : NO_TEAM;
    this.preview.update(myId, w, this.scene.get('game').cameras.main, (id) => {
      if (w.health[id] === 0) return 0;
      const teammate = myTeam !== NO_TEAM && w.team[id] === myTeam;
      if (!teammate && w.disguised[id] !== 0) return 0;
      return teammate ? (TEAM_DOT[myTeam] ?? 0x5b8fd6) : 0xe05a5a;
    });
  }

  // ---- feed -----------------------------------------------------------------

  private log(message: string, color?: string): void {
    this.feed.log(message, color);
  }

  private text(x: number, y: number, s: string, size: number, color?: string): Phaser.GameObjects.Text {
    return hudText(this, x, y, s, size, color);
  }
}
